import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type Currency, type ExchangeRate } from '../api.ts';
import { canInGroup, useMe } from '../auth.tsx';
import { changed, FormModal, FormSection } from '../components/FormModal.tsx';
import { ListToolbar } from '../components/ListFilters.tsx';
import { MasterDataNote, useMasterDataReadOnly } from '../components/master-data.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  issueFor,
  Loading,
  PageHeader,
  Select,
  Table,
  Textarea,
} from '../components/ui.tsx';
import { localized } from '../i18n/index.ts';
import { useFormat } from './purchasing/common.tsx';

const today = () => new Date().toISOString().slice(0, 10);

/** Efetivas (do dia) e as do planejamento (ADR 0032), na ordem das telas. */
const RATE_TYPES = ['mid', 'buy', 'sell', 'average', 'closing', 'budget'] as const;

function CurrencyForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const start = { code: '', pt: '', en: '', minorUnits: '2' };
  const [form, setForm] = useState(start);
  const save = useMutation({
    mutationFn: () =>
      api.post<Currency>('/core/currencies', {
        code: form.code.toUpperCase(),
        name: { 'pt-BR': form.pt, en: form.en },
        minorUnits: Number(form.minorUnits),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['currencies'] });
      onDone();
    },
  });
  const set = (k: keyof typeof form) => (ev: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: ev.target.value });

  return (
    <FormModal
      open
      title={t('currencies.new')}
      saveLabel={t('currencies.create')}
      dirty={changed(start, form)}
      saving={save.isPending}
      error={save.error}
      onSave={() => save.mutate()}
      onClose={onDone}
    >
      <FormSection>
        <Field
          label={t('fields.code')}
          hint={t('currencies.codeHint')}
          error={issueFor(save.error, 'code')}
          required
        >
          <Input id="cur-code" required maxLength={3} value={form.code} onChange={set('code')} />
        </Field>
        <Field
          label={t('currencies.minorUnits')}
          hint={t('currencies.minorUnitsHint')}
          error={issueFor(save.error, 'minorUnits')}
          required
        >
          <Input
            id="cur-minor"
            type="number"
            min={0}
            max={4}
            required
            value={form.minorUnits}
            onChange={set('minorUnits')}
          />
        </Field>
        <Field label={t('currencies.namePt')} error={issueFor(save.error, 'name')} required>
          <Input id="cur-name-pt" required value={form.pt} onChange={set('pt')} />
        </Field>
        <Field label={t('currencies.nameEn')} required>
          <Input id="cur-name-en" required value={form.en} onChange={set('en')} />
        </Field>
      </FormSection>
    </FormModal>
  );
}

function RateForm({ currencies, onDone }: { currencies: Currency[]; onDone: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [start] = useState(() => ({
    rateDate: today(),
    baseCurrency: 'USD',
    quoteCurrency: 'BRL',
    rate: '',
    rateType: 'mid',
  }));
  const [form, setForm] = useState(start);
  const save = useMutation({
    // A taxa vai como texto para não perder precisão em ponto flutuante.
    mutationFn: () =>
      api.post<ExchangeRate>('/core/exchange-rates', {
        ...form,
        rate: form.rate.replace(',', '.'),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['exchange-rates'] });
      onDone();
    },
  });
  const set =
    (k: keyof typeof form) => (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm({ ...form, [k]: ev.target.value });
  const options = currencies.filter((c) => c.isActive);

  return (
    <FormModal
      open
      title={t('rates.new')}
      saveLabel={t('rates.new')}
      description={t('rates.immutableNote')}
      dirty={changed(start, form)}
      saving={save.isPending}
      error={save.error}
      onSave={() => save.mutate()}
      onClose={onDone}
    >
      <FormSection>
        <Field label={t('rates.date')} error={issueFor(save.error, 'rateDate')} required>
          <Input
            id="rate-date"
            type="date"
            required
            value={form.rateDate}
            onChange={set('rateDate')}
          />
        </Field>
        <Field label={t('rates.type')} error={issueFor(save.error, 'rateType')} required>
          <Select id="rate-type" value={form.rateType} onChange={set('rateType')}>
            {RATE_TYPES.map((k) => (
              <option key={k} value={k}>
                {t(`rates.types.${k}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('rates.base')} error={issueFor(save.error, 'baseCurrency')} required>
          <Select id="rate-base" value={form.baseCurrency} onChange={set('baseCurrency')}>
            {options.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('rates.quote')} error={issueFor(save.error, 'quoteCurrency')} required>
          <Select id="rate-quote" value={form.quoteCurrency} onChange={set('quoteCurrency')}>
            {options.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t('rates.rate')}
          hint={t('rates.rateHint', { base: form.baseCurrency, quote: form.quoteCurrency })}
          error={issueFor(save.error, 'rate')}
          required
        >
          <Input
            id="rate-value"
            inputMode="decimal"
            required
            value={form.rate}
            onChange={set('rate')}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/**
 * Taxa de correção (ADR 0032): substitui uma taxa gravada, que continua no histórico. Quem corrige,
 * quando e por quê ficam registrados; valores já convertidos não mudam.
 */
function CorrectionForm({ rate, onDone }: { rate: ExchangeRate; onDone: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const start = { rate: '', reason: '' };
  const [form, setForm] = useState(start);
  const save = useMutation({
    mutationFn: () =>
      api.post<ExchangeRate>(`/core/exchange-rates/${rate.id}/correct`, {
        rate: form.rate.replace(',', '.'),
        reason: form.reason,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['exchange-rates'] });
      onDone();
    },
  });
  return (
    <FormModal
      open
      size="md"
      title={t('rates.correctTitle')}
      description={t('rates.correctNote', {
        date: rate.rateDate,
        pair: `${rate.baseCurrency}/${rate.quoteCurrency}`,
        type: t(`rates.types.${rate.rateType}`, { defaultValue: rate.rateType }),
        rate: rate.rate,
      })}
      dirty={changed(start, form)}
      saving={save.isPending}
      error={save.error}
      saveLabel={t('rates.correct')}
      onSave={() => save.mutate()}
      onClose={onDone}
    >
      <FormSection columns={1}>
        <Field label={t('rates.newRate')} error={issueFor(save.error, 'rate')} required>
          <Input
            id="rate-correct-value"
            inputMode="decimal"
            required
            value={form.rate}
            onChange={(ev) => setForm({ ...form, rate: ev.target.value })}
          />
        </Field>
        <Field
          label={t('rates.reason')}
          hint={t('rates.reasonHint')}
          error={issueFor(save.error, 'reason')}
          required
        >
          <Textarea
            id="rate-correct-reason"
            required
            minLength={3}
            maxLength={1000}
            value={form.reason}
            onChange={(ev) => setForm({ ...form, reason: ev.target.value })}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Uma taxa pela regra única (ADR 0089): gravada ou, a do mês, a média até ontem calculada. */
interface CurrentRate {
  rate: string;
  inverse: boolean;
  rateDate: string;
  source: string;
  provisional: boolean;
  days: number | null;
}
interface CurrentRates {
  routine: 'on' | 'off';
  lastRun: {
    date: string;
    currencies: { code: string; status: 'ok' | 'not_published' | 'failed' }[];
  } | null;
  items: { currency: string; day: CurrentRate | null; month: CurrentRate | null }[];
}

/** Fonte de uma taxa em palavras: PTAX do Banco Central, manual ou média até ontem. */
function SourceBadge({ source }: { source: string }) {
  const { t } = useTranslation();
  return (
    <Badge
      tone={source === 'BCB_PTAX' ? 'info' : source === 'MONTH_TO_DATE' ? 'accent' : 'neutral'}
    >
      {t(`rates.sources.${source}`, { defaultValue: source })}
    </Badge>
  );
}

/**
 * Taxas de hoje (ADR 0089): a taxa do dia e a do mês de cada moeda, pela mesma regra que todas
 * as frentes usam, com a fonte de cada uma. A média do mês até ontem é provisória e não é gravada.
 */
function CurrentRatesPanel() {
  const fmt = useFormat();
  const { t } = useTranslation();
  const current = useQuery({
    queryKey: ['exchange-rates', 'current'],
    queryFn: () => api.get<CurrentRates>('/core/exchange-rates/current'),
  });
  if (current.isPending) return <Loading />;
  if (current.isError) return <ErrorBox error={current.error} />;
  const { routine, lastRun, items } = current.data;
  const skipped = lastRun?.currencies.filter((c) => c.status === 'not_published') ?? [];
  const failed = lastRun?.currencies.filter((c) => c.status === 'failed') ?? [];
  const cell = (r: CurrentRate | null, currency: string) =>
    r ? (
      <div className="flex flex-col gap-1">
        <span className="font-mono">
          {r.inverse ? `1 BRL = ${r.rate} ${currency}` : `1 ${currency} = ${r.rate} BRL`}
        </span>
        <span className="flex flex-wrap items-center gap-1 text-xs text-muted">
          <SourceBadge source={r.source} />
          {r.provisional ? (
            <span>
              {t('rates.currentPanel.provisional', { count: r.days ?? 0, date: r.rateDate })}
            </span>
          ) : (
            <span>{fmt.date(r.rateDate)}</span>
          )}
        </span>
      </div>
    ) : (
      <span className="text-xs text-muted">{t('rates.currentPanel.none')}</span>
    );
  return (
    <>
      <p className="max-w-2xl text-sm text-muted">
        {routine === 'on' ? t('rates.currentPanel.routineOn') : t('rates.currentPanel.routineOff')}
        {skipped.length > 0 &&
          ` ${t('rates.currentPanel.notPublished', { codes: skipped.map((c) => c.code).join(', ') })}`}
        {failed.length > 0 &&
          ` ${t('rates.currentPanel.failed', { codes: failed.map((c) => c.code).join(', ') })}`}
      </p>
      <Table
        head={[
          t('rates.currentPanel.currency'),
          t('rates.currentPanel.day'),
          t('rates.currentPanel.month'),
        ]}
      >
        {items.length === 0 && <EmptyRow colSpan={3}>{t('rates.currentPanel.empty')}</EmptyRow>}
        {items.map((i) => (
          <tr key={i.currency}>
            <td className="font-mono">{i.currency}</td>
            <td>{cell(i.day, i.currency)}</td>
            <td>{cell(i.month, i.currency)}</td>
          </tr>
        ))}
      </Table>
    </>
  );
}

export function CurrenciesPage() {
  const fmt = useFormat();
  const { t } = useTranslation();
  const { data: me } = useMe();
  // Em FP&A › Configurações a tela só consulta: o cadastro é mantido em Sistema.
  const readOnly = useMasterDataReadOnly();
  const mayManage = canInGroup(me, 'core.currency.manage');
  const canManage = mayManage && !readOnly;
  const [open, setOpen] = useState<'currency' | 'rate' | null>(null);
  const [filter, setFilter] = useState({ base: '', quote: '', type: '' });
  const [correcting, setCorrecting] = useState<ExchangeRate | null>(null);
  const when = new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' });

  const currencies = useQuery({
    queryKey: ['currencies'],
    queryFn: () => api.get<Currency[]>('/core/currencies'),
  });
  const rates = useQuery({
    queryKey: ['exchange-rates', filter],
    queryFn: () => {
      const q = new URLSearchParams({ limit: '100' });
      if (filter.base) q.set('base', filter.base);
      if (filter.quote) q.set('quote', filter.quote);
      if (filter.type) q.set('type', filter.type);
      return api.get<ExchangeRate[]>(`/core/exchange-rates?${q}`);
    },
  });

  return (
    <>
      <PageHeader
        title={t('currencies.title')}
        description={t('currencies.description')}
        actions={
          canManage ? (
            <>
              <Button variant="secondary" onClick={() => setOpen('currency')}>
                {t('currencies.new')}
              </Button>
              <Button onClick={() => setOpen('rate')} disabled={!currencies.data}>
                {t('rates.new')}
              </Button>
            </>
          ) : null
        }
      />
      <MasterDataNote allowed={mayManage} />

      {open === 'currency' && <CurrencyForm onDone={() => setOpen(null)} />}
      {open === 'rate' && currencies.data && (
        <RateForm currencies={currencies.data} onDone={() => setOpen(null)} />
      )}
      {correcting && <CorrectionForm rate={correcting} onDone={() => setCorrecting(null)} />}

      <section className="flex flex-col gap-3">
        <h2 className="text-base font-semibold">{t('currencies.listTitle')}</h2>
        {currencies.isPending ? (
          <Loading />
        ) : currencies.isError ? (
          <ErrorBox error={currencies.error} />
        ) : (
          <Table
            head={[
              t('fields.code'),
              t('fields.name'),
              t('currencies.minorUnits'),
              t('fields.status'),
            ]}
          >
            {currencies.data.length === 0 && (
              <EmptyRow colSpan={4}>{t('currencies.empty')}</EmptyRow>
            )}
            {currencies.data.map((c) => (
              <tr key={c.code}>
                <td className="font-mono">{c.code}</td>
                <td>{localized(c.name)}</td>
                <td className="font-mono">{c.minorUnits}</td>
                <td>
                  {c.isActive ? (
                    <Badge tone="ok">{t('fields.active')}</Badge>
                  ) : (
                    <Badge tone="off">{t('fields.inactive')}</Badge>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold">{t('rates.currentPanel.title')}</h2>
          <p className="max-w-2xl text-sm text-muted">{t('rates.currentPanel.description')}</p>
        </div>
        <CurrentRatesPanel />
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h2 className="text-base font-semibold">{t('rates.title')}</h2>
            <p className="max-w-2xl text-sm text-muted">{t('rates.description')}</p>
          </div>
        </div>
        <ListToolbar>
          {(['base', 'quote'] as const).map((k) => (
            <Select
              key={k}
              id={`rate-filter-${k}`}
              className="w-full sm:w-56"
              aria-label={t(`rates.${k}`)}
              value={filter[k]}
              onChange={(ev) => setFilter({ ...filter, [k]: ev.target.value })}
            >
              <option value="">{t(`rates.all.${k}`)}</option>
              {currencies.data?.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code}
                </option>
              ))}
            </Select>
          ))}
          <Select
            id="rate-filter-type"
            className="w-full sm:w-56"
            aria-label={t('rates.type')}
            value={filter.type}
            onChange={(ev) => setFilter({ ...filter, type: ev.target.value })}
          >
            <option value="">{t('rates.all.type')}</option>
            {RATE_TYPES.map((k) => (
              <option key={k} value={k}>
                {t(`rates.types.${k}`)}
              </option>
            ))}
          </Select>
        </ListToolbar>
        {rates.isPending ? (
          <Loading />
        ) : rates.isError ? (
          <ErrorBox error={rates.error} />
        ) : (
          <Table
            head={[
              t('rates.date'),
              t('rates.pair'),
              t('rates.rate'),
              t('rates.type'),
              t('rates.source'),
              t('rates.situation'),
              t('rates.recordedBy'),
              '',
            ]}
          >
            {rates.data.length === 0 && <EmptyRow colSpan={8}>{t('rates.empty')}</EmptyRow>}
            {rates.data.map((r) => (
              <tr key={r.id}>
                <td className="whitespace-nowrap text-xs">{fmt.date(r.rateDate)}</td>
                <td className="font-mono">
                  1 {r.baseCurrency} = {r.quoteCurrency}
                </td>
                <td className="font-mono">{r.rate}</td>
                <td>{t(`rates.types.${r.rateType}`, { defaultValue: r.rateType })}</td>
                <td>
                  <SourceBadge source={r.source} />
                </td>
                <td>
                  {r.replacedById ? (
                    <Badge tone="off">{t('rates.replaced')}</Badge>
                  ) : (
                    <Badge tone="ok">{t('rates.current')}</Badge>
                  )}
                  {r.replacesRateId && (
                    <div className="mt-1 max-w-xs text-xs text-muted">
                      {t('rates.correctionOf')} {r.correctionReason}
                    </div>
                  )}
                </td>
                <td className="text-xs">
                  {r.createdByName ?? t('audit.system')}
                  <div className="text-muted">{when.format(new Date(r.createdAt))}</div>
                </td>
                <td>
                  {canManage && !r.replacedById && (
                    <Button variant="secondary" onClick={() => setCorrecting(r)}>
                      {t('rates.correct')}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </section>
    </>
  );
}
